/* eslint-disable react/prop-types */
/* eslint-disable react-native/no-inline-styles */

import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { StyleSheet, View, TouchableOpacity } from 'react-native';
import { isWeb } from '@global/screens';
import Colors from '@global/colors';
import Button from '@components/form/Button';
import SociollaText from '@components/sociolla/SociollaText';
import Table from '@components/table/Table';
import TableMobile from '@components/table/TableMobile';
import MoreButton from '@components/navbar/MoreButton';
import NotificationSettingButton from '@components/navbar/NotificationSettingButton';
import * as Layout from '@components/screen/Layout';
import StatusBadge from '@components/global/StatusBadge';
import Image from '@components/global/Image';
import StatusPicker from '@components/form/StatusPicker';
import { showPopup } from '@/store/modal';
import { filterAdded, paramsReseted } from '@/store/params';
import useListScreen from '@/hooks/useListScreen';
import {
  useGetCustomerTypeListQuery,
  useGetCustomerTypeCountQuery,
  useGetCustomerTypeInfiniteListInfiniteQuery,
} from '@api/listApi';

const tableStructure = {
  name: {
    head: 'Customer Type Name',
    body: 'name',
    textStyle: { fontFamily: 'figtree-medium' },
  },
  description: { head: 'Description', body: 'description' },
  is_active: { head: 'Status', body: 'is_active', type: 'status_active' },
};

const STATUSES = [
  { name: 'Active', value: true },
  { name: 'Inactive', value: false },
];

// Search box writes `name` (→ $regex); status filter passes is_active through.
const toApiFilter = (raw = {}) => {
  const filter = {};
  const name = raw.name?.$regex ?? raw.name;
  if (name) {
    filter.name = { $regex: name, $options: 'i' };
  }
  if (
    raw.is_active !== undefined &&
    raw.is_active !== '' &&
    raw.is_active !== null
  ) {
    filter.is_active = raw.is_active;
  }
  return filter;
};

const CustomerTypeScreen = (props) => {
  // ── selectors ──
  const dispatch = useDispatch();
  const userRole = useSelector((state) => state.auth.user.role);
  const showFilter = useSelector((state) => state.modal.showFilter);

  // ── local state ──
  const [draftFilter, setDraftFilter] = useState({});

  // ── derived ──
  const hasAddAccess = [
    'superadmin',
    'sales_admin',
    'marketing_admin',
    'general',
  ].includes(userRole && userRole.code);

  // ── list data ──
  const { list, count, openChild, scope, params } = useListScreen({
    listHook: useGetCustomerTypeListQuery,
    countHook: useGetCustomerTypeCountQuery,
    infiniteHook: useGetCustomerTypeInfiniteListInfiniteQuery,
    toFilter: toApiFilter,
  });

  // ── memoized values ──
  const searchValue = useMemo(() => {
    const k = params.filter?.name;
    return k && k.$regex ? k.$regex : k ?? '';
  }, [params.filter]);

  const rows = useMemo(() => {
    return isWeb
      ? Array.isArray(list.data)
        ? list.data
        : []
      : (list.data?.pages ?? []).flat();
  }, [list.data]);

  // ── handlers ──
  const goToDetail = ({ data }) => {
    openChild('CustomerTypeDetailScreen', {
      id: data._id,
      titleScreen: data.name,
    });
  };

  const goToForm = () => {
    openChild('CustomerTypeFormScreen');
  };

  // ── effects ──
  // Own the header so the mobile Add goes through openChild (preserves the list
  // on return) rather than the navigator's plain navigate.
  useEffect(() => {
    props.navigation.setOptions({
      headerRight: () =>
        isWeb ? (
          <NotificationSettingButton {...props} />
        ) : hasAddAccess ? (
          <MoreButton
            menu={[{ type: 'function', name: '+ Add', callback: goToForm }]}
            {...props}
          />
        ) : (
          false
        ),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.navigation, hasAddAccess]);

  // Seed the staged filter draft from the applied filter whenever the modal opens.
  useEffect(() => {
    if (showFilter) {
      setDraftFilter(params.filter || {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showFilter]);

  // ── render ──
  return (
    <Layout.Container>
      {isWeb ? (
        <>
          <Layout.Title title="Customer Type" style={{ zIndex: 1000 }}>
            {hasAddAccess && (
              <Button
                type="primary"
                rounded
                buttonStyle={{ width: 120 }}
                title={
                  <SociollaText
                    type="medium"
                    style={{ fontSize: 14, color: Colors.white }}>
                    + Add
                  </SociollaText>
                }
                onPress={() => goToForm()}
              />
            )}
          </Layout.Title>

          <Table
            filterName={scope}
            searchKey="name"
            searchPlaceholder="Search by name"
            structures={tableStructure}
            data={rows}
            loading={list.isLoading}
            refetching={list.isFetching}
            hasNoHide={true}
            totalData={count.data}
            skip={params.skip}
            limit={params.limit}
            hasCallback={true}
            name="customerTypes"
            showFilter={true}
            onClickRow={(val) => goToDetail(val)}
            searchValue={searchValue}
          />
        </>
      ) : (
        <TableMobile
          filterName={scope}
          searchKey="name"
          placeholder="Search by name"
          loading={list.isLoading}
          refetching={list.isFetching}
          loadingLoadMore={list.isFetchingNextPage}
          hasNextPage={list.hasNextPage}
          onLoadMore={list.fetchNextPage}
          onRefresh={list.refetch}
          data={rows}
          totalData={count.data}
          searchValue={searchValue}
          renderItem={({ data, index }) => (
            <TouchableOpacity
              key={index}
              activeOpacity={0.6}
              onPress={() => goToDetail({ data: data })}
              style={styles.itemContainer}>
              <View style={styles.itemHeader}>
                <SociollaText type="medium">{data.name || '-'}</SociollaText>
                <StatusBadge status={data.is_active ? 'active' : 'in_active'} />
              </View>
              <View style={styles.itemBody}>
                <Image
                  width={18}
                  height={18}
                  svg
                  style={{ marginRight: 5 }}
                  source={require('@assets/img/icons/menu/product-catalog.svg')}
                />
                <SociollaText style={{ color: Colors.grey1, fontSize: 12 }}>
                  {data.description || '-'}
                </SociollaText>
              </View>
            </TouchableOpacity>
          )}
        />
      )}
      <Layout.Filter
        name="customerTypes"
        filterName={scope}
        isLocalVariable={true}
        hasFilterData={Object.keys(draftFilter).length > 0}
        onReset={() => {
          if (list.isFetching) {
            return;
          }
          setDraftFilter({});
          dispatch(paramsReseted({ key: scope }));
        }}
        onApply={() => {
          if (list.isFetching) {
            return;
          }
          dispatch(filterAdded({ key: scope, filter: draftFilter }));
          dispatch(showPopup({ name: 'showFilter', value: false }));
        }}>
        <Layout.FilterItem lastItem>
          <StatusPicker
            label="Status"
            options={STATUSES}
            value={STATUSES.find((s) => s.value === draftFilter.is_active)}
            trackBy="value"
            labelKey="name"
            onChange={(val) =>
              setDraftFilter((f) => ({ ...f, is_active: val.value }))
            }
          />
        </Layout.FilterItem>
      </Layout.Filter>
    </Layout.Container>
  );
};

const styles = StyleSheet.create({
  itemContainer: {
    width: '100%',
    paddingVertical: 20,
    paddingHorizontal: 10,
    backgroundColor: Colors.white,
    borderBottomWidth: 1,
    borderBottomColor: Colors.grey0,
  },
  itemHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 10,
  },
  itemBody: {
    flexDirection: 'row',
    alignItems: 'center',
  },
});

export default CustomerTypeScreen;
